"use client";

import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const methodologySteps = [{
  phase: "Phase 01",
  stage: "Challenge",
  title: "Understanding the Real Bottlenecks",
  description: "I begin by diagnosing key organic obstacles—whether technical debt, algorithmic drops, indexing hurdles, or aggressive competitor positioning.",
  highlight: "Comprehensive Technical & Search Audit"
}, {
  phase: "Phase 02",
  stage: "Strategy",
  title: "Designing a Focused Roadmap",
  description: "I craft a bespoke search strategy tailored specifically to your target audience, commercial priorities, and long-term business goals.",
  highlight: "Custom Action Plan & Strategy Blueprint"
}, {
  phase: "Phase 03",
  stage: "Work Delivered",
  title: "Hands-on Direct Execution",
  description: "I execute recommendations directly—optimizing site architecture, refining on-page structure, and building modern AI search visibility.",
  highlight: "Direct Consultant Implementation"
}, {
  phase: "Phase 04",
  stage: "Verified Results",
  title: "Transparent & Clear Tracking",
  description: "I measure organic performance using clean, unmanipulated data—tracking keyword movement, indexation quality, and traffic growth.",
  highlight: "Honest Measurement & Clear Reporting"
}, {
  phase: "Phase 05",
  stage: "Business Impact",
  title: "Driving Sustainable Commercial Value",
  description: "The ultimate goal is compounding organic acquisition—turning search visibility into qualified inquiries and measurable revenue.",
  highlight: "Compounding Organic Growth & Authority"
}];
export default function Results() {
  const prefersReducedMotion = useReducedMotion();
  return <section data-section-id="22976" id="real-results-for-real-businesses" data-nav="dark" className="bg-[hsl(240,9%,13%)] text-white py-24 md:py-36 border-b border-[hsl(240,6%,25%)] overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 items-end">
          <div className="lg:col-span-4">
            <div className="flex flex-col items-start">
              <svg aria-hidden="true" viewBox="0 0 140 12" className="w-24 h-2.5 mb-2.5 text-[hsl(340,75%,65%)]" fill="none">
                <path d="M2 7 C 40 2, 90 10, 138 5" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
              </svg>

              <span className="font-sans font-semibold text-xs uppercase tracking-wide text-[hsl(340,75%,65%)]">
                Methodology framework
              </span>

              <h2 className="mt-4 font-serif font-extrabold text-[clamp(1.8125rem,2.8vw,2.625rem)] tracking-tight leading-[1.08] text-white">
                How I{" "}
                <span className="relative inline-block italic font-normal text-white">
                  work.
                </span>{" "}
                My consulting process.
              </h2>
            </div>
          </div>

          <div className="lg:col-span-8 lg:pb-3">
            <p className="font-sans font-normal text-[clamp(1.25rem,0.55vw+1.125rem,1.375rem)] text-[hsl(0,0%,85%)] leading-relaxed max-w-2xl">
              I follow a structured five-stage framework to analyze, strategize, execute, and verify SEO results for every business I consult with.
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {methodologySteps.map((step, i) => <motion.div key={step.stage} data-index={i} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.6,
          delay: prefersReducedMotion ? 0 : i * 0.1,
          ease: "easeOut"
        }} className="flex flex-col justify-between rounded-lg border border-[hsl(240,6%,28%)] bg-[hsl(240,9%,18%)] p-6 sm:p-8 transition-[border-color,box-shadow,background-color] duration-300 hover:border-[hsl(340,75%,65%)] hover:shadow-lg">
              <div>
                <div className="flex items-center justify-between gap-4">
                  <Badge className="rounded-md bg-[hsl(340,40%,25%)] border border-[hsl(340,75%,65%)/30] px-2.5 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-[hsl(340,75%,75%)]">
                    {step.phase}
                  </Badge>
                  <span className="font-sans text-xs font-semibold uppercase tracking-wider text-[hsl(340,75%,65%)]">
                    {step.stage}
                  </span>
                </div>

                <h3 className="mt-6 font-serif font-bold text-xl sm:text-2xl leading-snug text-white">
                  {step.title}
                </h3>

                <p className="mt-4 font-sans text-sm leading-relaxed text-[hsl(0,0%,75%)]">
                  {step.description}
                </p>
              </div>

              <div className="mt-8 border-t border-[hsl(240,6%,28%)] pt-4 flex items-center justify-between text-xs font-medium">
                <span className="text-[hsl(0,0%,70%)] font-sans">{step.highlight}</span>
                <ArrowUpRight className="size-4 text-[hsl(340,75%,65%)] shrink-0" />
              </div>
            </motion.div>)}
        </div>

        <div className="mt-16 md:mt-24 flex flex-col items-center justify-center gap-6 sm:flex-row">
          <Button asChild className="h-auto rounded-md bg-primary px-8 py-4 font-sans text-[17px] font-semibold text-primary-foreground shadow transition-[background-color,box-shadow] duration-300 hover:bg-primary/90">
            <Link to="/CaseStudiesPage">Explore all studies</Link>
          </Button>

          <Link to="/CaseStudiesPage" className="inline-flex items-center gap-1.5 font-sans text-[17px] font-semibold text-[hsl(340,75%,65%)] transition-colors duration-200 hover:text-white">
            <span>View my full portfolio</span>
            <ArrowUpRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>;
}